'use client';

import { runningClientSide } from '@benzinga/utils';
import type { EdgeIframeCheckoutParams } from './buildEdgeIframeCheckoutUrl';

export type EdgeCheckoutOpenOptions = {
  checkoutParams: EdgeIframeCheckoutParams;
  checkoutSessionId?: string;
  checkoutType: string;
  onUnlock?: () => void;
  paywallId: string;
  placement?: string;
};

type EdgeCheckoutListener = (options: EdgeCheckoutOpenOptions) => void;

let listener: EdgeCheckoutListener | null = null;
let pendingOpen: EdgeCheckoutOpenOptions | null = null;

export const EDGE_CHECKOUT_OPEN_EVENT = 'benzinga-edge-checkout:open';

export const openEdgeCheckout = (options: EdgeCheckoutOpenOptions): void => {
  if (listener) {
    listener(options);
    return;
  }
  pendingOpen = options;
};

export const subscribeEdgeCheckout = (nextListener: EdgeCheckoutListener): (() => void) => {
  listener = nextListener;
  if (pendingOpen) {
    nextListener(pendingOpen);
    pendingOpen = null;
  }
  return () => {
    if (listener === nextListener) {
      listener = null;
    }
  };
};

export const registerEdgeCheckoutGlobal = (): (() => void) => {
  if (!runningClientSide()) {
    return () => undefined;
  }

  window.benzingaEdgeCheckout = { open: openEdgeCheckout };

  const handleOpenEvent = (event: Event) => {
    const customEvent = event as CustomEvent<EdgeCheckoutOpenOptions>;
    if (!customEvent.detail) return;
    openEdgeCheckout(customEvent.detail);
  };

  document.addEventListener(EDGE_CHECKOUT_OPEN_EVENT, handleOpenEvent);

  return () => {
    delete window.benzingaEdgeCheckout;
    document.removeEventListener(EDGE_CHECKOUT_OPEN_EVENT, handleOpenEvent);
  };
};

declare global {
  interface Window {
    benzingaEdgeCheckout?: {
      open: (options: EdgeCheckoutOpenOptions) => void;
    };
  }
}
